import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const EMPTY = { name: "", email: "", phone: "", company: "", inquiry_type: "general", message: "" };

export default function ContactForm({ responseNote }) {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setForm(EMPTY);
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const wrapper = "border border-border bg-card/40 p-8 md:p-11";

  if (sent) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        data-testid="contact-success-panel"
        className={`${wrapper} flex min-h-[420px] flex-col items-start justify-center`}
        style={{ borderRadius: "var(--radius)" }}
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <CheckCircle2 className="h-7 w-7" />
        </span>
        <h3 className="mt-7 font-head text-3xl md:text-4xl">{t("contact_success_title")}</h3>
        <p className="mt-4 max-w-md text-muted-foreground leading-relaxed">{t("form_success")}</p>
        {responseNote && <p className="mt-3 max-w-md text-sm text-muted-foreground/80 leading-relaxed">{responseNote}</p>}
        <button type="button" data-testid="contact-send-another" onClick={() => setSent(false)} className="btn-outline mt-9 h-11 px-6 text-xs">
          {t("contact_send_another")}
        </button>
      </motion.div>
    );
  }

  const inquiryOptions = [
    { value: "general", label: t("inquiry_general") },
    { value: "investment", label: t("inquiry_investment") },
    { value: "partnership", label: t("inquiry_partnership") },
    { value: "media", label: t("inquiry_media") },
  ];

  return (
    <form data-testid="contact-form" onSubmit={submit} className={`grid gap-6 ${wrapper}`} style={{ borderRadius: "var(--radius)" }}>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="cf-name">{t("form_name")}</Label>
          <Input id="cf-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="cf-email">{t("form_email")}</Label>
          <Input id="cf-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="cf-phone">{t("form_phone")}</Label>
          <Input id="cf-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="cf-company">{t("form_company")}</Label>
          <Input id="cf-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} />
        </div>
        <div className="grid gap-2 md:col-span-2">
          <Label>{t("form_inquiry")}</Label>
          <Select value={form.inquiry_type} onValueChange={(v) => setForm((f) => ({ ...f, inquiry_type: v }))}>
            <SelectTrigger data-testid="contact-inquiry-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {inquiryOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2 md:col-span-2">
          <Label htmlFor="cf-message">{t("form_message")}</Label>
          <Textarea id="cf-message" data-testid="contact-message-input" required rows={6} value={form.message} onChange={set("message")} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-5">
        <button type="submit" data-testid="contact-submit-button" disabled={sending} className="btn-gold disabled:opacity-60 w-full sm:w-auto">
          {sending ? t("form_sending") : t("form_send")}
        </button>
        {responseNote && <p className="max-w-sm text-xs text-muted-foreground leading-relaxed" data-testid="contact-response-note">{responseNote}</p>}
      </div>
    </form>
  );
}
